import { useState, useEffect } from 'react';
import { Search, Plus, Edit, X, Copy, Check } from 'lucide-react';
import { adminApi, type CreateUserRequest } from '../services/adminApi';

interface User {
  id: string;
  email: string;
  full_name: string;
  organization?: string;
  role: string;
  subscription_tier?: string;
  created_at: string;
  is_active: boolean;
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdUserData, setCreatedUserData] = useState<any>(null);

  useEffect(() => {
    loadUsers();
  }, [searchQuery]);

  const loadUsers = async () => {
    try {
      const response = await adminApi.listUsers(1, 50, { search: searchQuery });
      setUsers(response.users || response.data || []);
    } catch (error) {
      console.error('[USER MANAGEMENT] Failed to load users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (userData: CreateUserRequest, generatedPassword: string) => {
    try {
      const response = await adminApi.createUser(userData);
      setCreatedUserData({ ...response, password: generatedPassword });
      setShowCreateModal(false);
      setShowSuccessModal(true);
      loadUsers(); // Refresh list
    } catch (error: any) {
      alert(`Failed to create user: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEditUser = (user: User) => {
    console.log('[USER MANAGEMENT] Edit user:', user.id);
    alert(`Edit user: ${user.full_name} - Feature coming soon`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-normal tracking-tight text-foreground mb-1">
            User Management
          </h1>
          <p className="text-body-small text-muted-foreground font-normal tracking-tight">
            Create and manage customer accounts
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-1.5 bg-primary text-primary-foreground text-body-small font-normal tracking-tight rounded-md hover:bg-primary/90 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create New User
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search users by email, name, or company..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-3 py-2 bg-background border border-input rounded-md text-body-small text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
        />
      </div>

      {/* Users Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <p className="text-body-small text-muted-foreground p-6">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-body-small text-muted-foreground p-6">No users found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-normal tracking-tight text-muted-foreground py-3 px-4 bg-muted/30">Name</th>
                  <th className="text-left text-xs font-normal tracking-tight text-muted-foreground py-3 px-4 bg-muted/30">Email</th>
                  <th className="text-left text-xs font-normal tracking-tight text-muted-foreground py-3 px-4 bg-muted/30">Company</th>
                  <th className="text-left text-xs font-normal tracking-tight text-muted-foreground py-3 px-4 bg-muted/30">Tier</th>
                  <th className="text-left text-xs font-normal tracking-tight text-muted-foreground py-3 px-4 bg-muted/30">Created</th>
                  <th className="text-left text-xs font-normal tracking-tight text-muted-foreground py-3 px-4 bg-muted/30">Status</th>
                  <th className="text-right text-xs font-normal tracking-tight text-muted-foreground py-3 px-4 bg-muted/30">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                    <td className="text-body-small font-normal tracking-tight text-foreground py-3 px-4">{user.full_name}</td>
                    <td className="text-body-small font-normal tracking-tight text-foreground py-3 px-4">{user.email}</td>
                    <td className="text-body-small font-normal tracking-tight text-foreground py-3 px-4">{user.organization || '—'}</td>
                    <td className="py-3 px-4">
                      <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">
                        {user.subscription_tier || 'Free'}
                      </span>
                    </td>
                    <td className="text-body-small font-normal tracking-tight text-foreground py-3 px-4">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <button 
                        onClick={() => handleEditUser(user)}
                        className="text-muted-foreground hover:text-foreground p-1 hover:bg-accent rounded transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && createdUserData && (
        <SuccessModal
          userData={createdUserData}
          onClose={() => {
            setShowSuccessModal(false);
            setCreatedUserData(null);
          }}
        />
      )}
    </div>
  );
}

// Create User Modal Component
function CreateUserModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (data: CreateUserRequest, password: string) => void }) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company_name: '',
    workspace_name: '',
    password: '',
    subscription_tier: 'professional' as 'free' | 'business' | 'professional' | 'enterprise',
    monthly_token_limit: 999999999,
  });

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const userData: CreateUserRequest = {
      email: formData.email,
      password: formData.password,
      full_name: formData.full_name,
      workspace_name: formData.workspace_name || formData.company_name,
      company_name: formData.company_name,
      role: 'admin',
      subscription_tier: formData.subscription_tier,
      monthly_token_limit: formData.monthly_token_limit,
    };

    onSubmit(userData, formData.password);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-normal tracking-tight text-foreground">
            Create New Customer Account
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 hover:bg-accent rounded transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Customer Information */}
          <div>
            <h3 className="text-sm font-normal tracking-tight text-gray-900 mb-4">Customer Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-normal tracking-tight text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="admin-input"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-normal tracking-tight text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="admin-input"
                  placeholder="john@acmecorp.com"
                />
              </div>
              <div>
                <label className="block text-sm font-normal tracking-tight text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="admin-input"
                  placeholder="Acme Corporation"
                />
              </div>
            </div>
          </div>

          {/* Account Setup */}
          <div>
            <h3 className="text-sm font-normal tracking-tight text-gray-900 mb-4">Account Setup</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-normal tracking-tight text-gray-700 mb-2">
                  Workspace Name
                </label>
                <input
                  type="text"
                  value={formData.workspace_name}
                  onChange={(e) => setFormData({ ...formData, workspace_name: e.target.value })}
                  className="admin-input"
                  placeholder="Acme Corp (defaults to company name)"
                />
              </div>
              <div>
                <label className="block text-sm font-normal tracking-tight text-gray-700 mb-2">
                  Password
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="admin-input flex-1"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="admin-button-secondary whitespace-nowrap"
                  >
                    Generate Strong
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div>
            <h3 className="text-sm font-normal tracking-tight text-gray-900 mb-4">Subscription</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-normal tracking-tight text-gray-700 mb-2">
                  Tier
                </label>
                <select
                  value={formData.subscription_tier}
                  onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value as any })}
                  className="admin-input"
                >
                  <option value="free">Free</option>
                  <option value="business">Business</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-normal tracking-tight text-gray-700 mb-2">
                  Monthly Token Limit
                </label>
                <input
                  type="number"
                  value={formData.monthly_token_limit}
                  onChange={(e) => setFormData({ ...formData, monthly_token_limit: parseInt(e.target.value) })}
                  className="admin-input"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="admin-button-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="admin-button flex-1">
              Create Account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Success Modal Component
function SuccessModal({ userData, onClose }: { userData: any; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const copyPassword = () => {
    navigator.clipboard.writeText(userData.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-md w-full p-6">
        {/* Success Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <h2 className="text-xl font-normal tracking-tight text-gray-900 mb-2">
            Account Created Successfully
          </h2>
        </div>

        {/* Credentials */}
        <div className="space-y-4 mb-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Customer can now log in at:</p>
            <p className="text-sm font-normal tracking-tight text-gray-900">
              🔗 https://cyrenza.com/auth/login
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Login Credentials:</p>
            <div className="bg-gray-50 p-3 space-y-2">
              <p className="text-sm font-normal tracking-tight">
                <span className="text-gray-600">Email:</span> {userData.user?.email || userData.email}
              </p>
              <div className="flex items-center justify-between">
                <p className="text-sm font-normal tracking-tight">
                  <span className="text-gray-600">Password:</span> {userData.password}
                </p>
                <button
                  onClick={copyPassword}
                  className="text-gray-600 hover:text-gray-900 p-1"
                  title="Copy password"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-3">
            <p className="text-xs text-yellow-800">
              ⚠️ IMPORTANT: Send password via secure channel (encrypted email, password manager)
            </p>
          </div>
        </div>

        {/* Actions */}
        <button onClick={onClose} className="admin-button w-full">
          Close
        </button>
      </div>
    </div>
  );
}
